import { useState } from "react";
import { Button, Input, Modal } from "../ui";

export const EMPTY_STUDENT = { regNumber: "", nationalId: "", firstName: "", lastName: "", email: "", programme: "", yearOfStudy: "1", tuitionFee: "0", allowance: "0" };

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const WHOLE = /^\d+$/;

/**
 * What the API would refuse in one student, by field: the same limits as the Loans service's
 * list entries. `taken` holds the other students' registration numbers, upper-cased.
 */
export const studentErrors = (s, taken) => {
  const e = {};
  const reg = String(s.regNumber ?? "").trim();
  if (!reg) e.regNumber = "Enter the registration number.";
  else if (reg.length > 50) e.regNumber = "At most 50 characters.";
  else if (taken.has(reg.toUpperCase())) e.regNumber = "This student is already on the list.";
  if (!/^\d{16}$/.test(String(s.nationalId ?? "").trim())) e.nationalId = "16 digits.";
  if (!String(s.firstName ?? "").trim()) e.firstName = "Enter the first name.";
  if (!String(s.lastName ?? "").trim()) e.lastName = "Enter the last name.";
  if (String(s.email ?? "").trim() && !EMAIL.test(String(s.email).trim())) e.email = "Enter a valid email address.";
  if (!String(s.programme ?? "").trim()) e.programme = "Enter the programme.";
  const year = Number(s.yearOfStudy);
  if (!WHOLE.test(String(s.yearOfStudy ?? "").trim()) || year < 1 || year > 10) e.yearOfStudy = "A year from 1 to 10.";
  if (!WHOLE.test(String(s.tuitionFee ?? "").trim())) e.tuitionFee = "A whole amount in RWF, 0 or more.";
  if (!WHOLE.test(String(s.allowance ?? "").trim())) e.allowance = "A whole amount in RWF, 0 or more.";
  return e;
};

// A student as the API takes it
export const toEntry = (s) => ({
  regNumber: String(s.regNumber).trim(),
  nationalId: String(s.nationalId).trim(),
  firstName: String(s.firstName).trim(),
  lastName: String(s.lastName).trim(),
  email: String(s.email ?? "").trim() || null,
  programme: String(s.programme).trim(),
  yearOfStudy: Number(s.yearOfStudy),
  tuitionFee: Number(s.tuitionFee),
  allowance: Number(s.allowance),
});

// Adds a student to a draft list, or changes one already on it
export default function StudentDialog({ student, taken, onSave, onClose }) {
  const [form, setForm] = useState(() =>
    student ? { ...student, email: student.email ?? "", yearOfStudy: String(student.yearOfStudy), tuitionFee: String(student.tuitionFee), allowance: String(student.allowance) } : EMPTY_STUDENT
  );
  const [errors, setErrors] = useState({});

  const update = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => (prev[name] ? { ...prev, [name]: undefined } : prev));
  };

  const save = (e) => {
    e.preventDefault();
    const found = studentErrors(form, taken);
    if (Object.keys(found).length) return setErrors(found);
    onSave(toEntry(form));
  };

  const field = (name, label, props = {}) => <Input name={name} label={label} value={form[name]} onChange={update} error={errors[name]} {...props} />;

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={student ? "Edit student" : "Add a student"}
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" type="submit" form="private-list-student">
            {student ? "Update student" : "Add student"}
          </Button>
        </>
      }
    >
      <form id="private-list-student" onSubmit={save} noValidate className="grid gap-4 sm:grid-cols-2">
        {field("regNumber", "Registration number", { required: true, maxLength: 50 })}
        {field("nationalId", "National ID", { required: true, inputMode: "numeric", maxLength: 16 })}
        {field("firstName", "First name", { required: true, maxLength: 100 })}
        {field("lastName", "Last name", { required: true, maxLength: 100 })}
        {field("email", "Email", { type: "email", maxLength: 200 })}
        {field("programme", "Programme", { required: true, maxLength: 200 })}
        {field("yearOfStudy", "Year of study", { required: true, type: "number", min: 1, max: 10 })}
        {field("tuitionFee", "Tuition fee (RWF)", { required: true, type: "number", min: 0 })}
        {field("allowance", "Allowance (RWF)", { required: true, type: "number", min: 0 })}
      </form>
    </Modal>
  );
}
